import React, { useCallback, useEffect, useState } from 'react';
import { CheckCircle2, Download, Loader2, Plus, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  apiErrorMessage, changeLibraryItem, fetchCapabilityLibrary, importIntoLibrary, inspectForLibrary,
  type DetectedImportItem, type LibraryChange, type LibraryItem, type LibraryView,
} from '../../services/capabilitiesApi';
import { Action, EmptyState, ErrorNote, FilterChips, Kicker, Loading, Modal, RouteMark, Tabs, Toggle } from '../aa';
import { CapabilityDetails } from '../capabilities/CapabilityDetails';

/*
 * Skills and plugins, for the person. The same list the builder's Settings
 * shows, read from the same table: Agents Anywhere's own, the company store,
 * and their own. Whatever anyone imports goes into the company store, and
 * each person switches on what they want. No department or permission gate.
 */

const HEAD = 'px-3 py-2.5 text-left text-[12.5px] font-medium text-ink-muted';
const CELL = 'px-3 py-3 text-[13.5px] text-ink';

const FORMAT_LABEL: Record<string, string> = {
  claude: 'Claude Code',
  codex: 'Codex',
  agentskills: 'Agent Skills',
  agentsanywhere: 'Agents Anywhere',
};

type Kind = 'skill' | 'plugin';
type Shelf = 'mine' | 'store';

/* Pushing to a repository is the importer's alone. An item the company
   library held before owners were recorded falls to whoever runs the library. */
const canPushItem = (item: LibraryItem, canManage: boolean): boolean => {
  if (item.providedBy === 'aa') return false;
  if (item.isMine) return true;
  return !item.ownerEmail && canManage;
};

/* Who provides it, as a name: "Me" for your own, the person who shared it
   otherwise, Agents Anywhere for the platform's, Company when nobody is
   recorded. */
const providedName = (item: LibraryItem): string => {
  if (item.isMine) return 'Me';
  if (item.providedBy === 'aa') return 'Agents Anywhere';
  if (item.ownerEmail) return item.ownerName || item.ownerEmail;
  return 'Company';
};

const providedLine = (item: LibraryItem): string => {
  if (item.isMine && !item.ownerOn) return 'Switched off for now';
  if (item.isMine) return 'In your sessions';
  if (!item.onForMe) return 'Off until you switch it on';
  if (item.providedBy === 'aa') return 'In your sessions';
  if (item.ownerEmail && item.ownerRole) return `${item.ownerRole} · in your sessions`;
  return 'In your sessions';
};

const selfChange = (next: boolean): LibraryChange => {
  if (next) return 'self_on';
  return 'self_off';
};

const ImportDialog: React.FC<{ clientId: string; onClose: () => void; onImported: () => void }> = ({ clientId, onClose, onImported }) => {
  const [source, setSource] = useState('');
  const [phase, setPhase] = useState<'input' | 'inspecting' | 'select' | 'importing'>('input');
  const [detected, setDetected] = useState<DetectedImportItem[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const working = phase === 'inspecting' || phase === 'importing';

  const inspect = async () => {
    if (!source.trim()) return;
    setPhase('inspecting');
    setError(null);
    try {
      const items = await inspectForLibrary(clientId, source.trim());
      setDetected(items);
      setSelected(new Set());
      setPhase('select');
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not inspect that repository.'));
      setPhase('input');
    }
  };

  const runImport = async () => {
    setPhase('importing');
    setError(null);
    try {
      await importIntoLibrary(clientId, source.trim(), Array.from(selected));
      toast.success(`${selected.size} imported. In your sessions from your next message.`);
      onImported();
    } catch (e) {
      setError(apiErrorMessage(e, 'Import failed.'));
      setPhase('select');
    }
  };

  const flip = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  let importLabel = 'Import';
  if (selected.size > 0) importLabel = `Import ${selected.size}`;
  const selectable = detected.filter((i) => !i.blank).map((i) => i.key);
  const allOn = selectable.length > 0 && selectable.every((k) => selected.has(k));
  const toggleAll = () => {
    if (allOn) {
      setSelected(new Set());
      return;
    }
    setSelected(new Set(selectable));
  };
  let allLabel = 'Select all';
  if (allOn) allLabel = 'Clear';

  return (
    <Modal size="dialog" onClose={onClose} context={<Kicker>Import your own</Kicker>} footer={(
      <div className="flex justify-end gap-2">
        <Action variant="quiet" onClick={onClose}>Cancel</Action>
        <Action disabled={selected.size === 0 || phase !== 'select'} reason="Inspect a repository first" onClick={() => void runImport()}>
          {phase === 'importing' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {phase !== 'importing' && <Download className="h-3.5 w-3.5" />}
          {importLabel}
        </Action>
      </div>
    )}>
      <h2 className="m-0 text-[24px] font-bold leading-[31px] tracking-[-0.015em] text-ink">Skills and plugins for you</h2>
      <p className="m-0 mt-2 text-[14px] leading-[23px] text-ink-muted">Paste a public GitHub repo. We detect Agent Skills, Claude Code plugins and marketplaces, and Codex plugins. They run in your sessions and go into the company store, where anyone can switch them on for themselves.</p>
      <div className="mt-6 flex items-center gap-2">
        <input
          value={source}
          onChange={(e) => setSource(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') void inspect(); }}
          placeholder="owner/repo or https://github.com/owner/repo"
          disabled={working}
          autoComplete="off"
          className="w-full rounded-act border border-input bg-white px-3 py-2 text-[13px] text-ink outline-none focus:border-ink"
        />
        <Action variant="quiet" disabled={!source.trim() || working} onClick={() => void inspect()}>
          {phase === 'inspecting' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {phase !== 'inspecting' && <Search className="h-3.5 w-3.5" />}
          Inspect
        </Action>
      </div>
      {error && <ErrorNote className="mt-3" message={error} />}
      {(phase === 'select' || phase === 'importing') && (
        <div className="mt-5">
          <div className="flex items-center justify-between gap-3">
            <Kicker tone="muted">Found {detected.length} · {selected.size} selected</Kicker>
            <button type="button" onClick={toggleAll} disabled={working} className="text-[12px] font-semibold text-ink underline underline-offset-2 disabled:opacity-50">{allLabel}</button>
          </div>
          <ul className="mt-2 divide-y divide-line-soft rounded-act border border-line">
            {detected.map((item) => {
              const on = selected.has(item.key);
              const format = FORMAT_LABEL[item.format] || '';
              let kindText = 'Skill';
              if (item.type === 'plugin') kindText = 'Plugin';
              let rowClass = 'hover:bg-canvas';
              if (on) rowClass = 'bg-canvas-inset';
              let boxClass = 'border-line-strong';
              if (on) boxClass = 'border-ink bg-ink text-white';
              return (
                <li key={item.key}>
                  <button type="button" onClick={() => flip(item.key)} className={`flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors ${rowClass}`}>
                    <span className={`mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border ${boxClass}`}>
                      {on && <CheckCircle2 className="h-3 w-3" />}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-[12.5px] font-semibold text-ink">{item.name}</span>
                        <span className="rounded-md bg-canvas-inset px-2 py-0.5 text-[10.5px] font-semibold text-ink-muted">{kindText}{format && ` · ${format}`}</span>
                        {item.blank && <span className="text-[10.5px] font-semibold text-[#8A6D00]">Blank template</span>}
                      </span>
                      {item.description && <span className="mt-0.5 line-clamp-2 text-[11.5px] leading-snug text-ink-dim">{item.description}</span>}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Modal>
  );
};

export const CapabilitiesPanel: React.FC<{ clientId: string }> = ({ clientId }) => {
  const [view, setView] = useState<LibraryView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState<Kind>('skill');
  const [shelf, setShelf] = useState<Shelf>('mine');
  const [importing, setImporting] = useState(false);
  const [open, setOpen] = useState<LibraryItem | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await fetchCapabilityLibrary(clientId));
      setError(null);
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not load skills and plugins.'));
    }
  }, [clientId]);

  useEffect(() => { void load(); }, [load]);

  /* The switch moves the moment it is clicked; the reload after the call
     settles the rest of the row, and a failed call reloads to the truth. */
  const flipLocally = (id: string, patch: Partial<LibraryItem>) => {
    setView((prev) => {
      if (!prev) return prev;
      const apply = (items: LibraryItem[]) => items.map((i) => (i.id === id ? { ...i, ...patch } : i));
      return { ...prev, aa: apply(prev.aa), company: apply(prev.company), mine: apply(prev.mine) };
    });
  };

  const change = async (item: LibraryItem, what: LibraryChange) => {
    if (what === 'remove' && !window.confirm(`Remove ${item.name}? It leaves the company store and every session that had it.`)) return;
    if ((what === 'self_on' || what === 'self_off') && item.isMine) flipLocally(item.id, { ownerOn: what === 'self_on', onForMe: what === 'self_on' });
    if ((what === 'self_on' || what === 'self_off') && !item.isMine) flipLocally(item.id, { onForMe: what === 'self_on' });
    setBusy(item.id);
    try {
      await changeLibraryItem(clientId, item.id, what);
      if (what === 'self_on') toast.success(`${item.name} is in your sessions from your next message.`);
      if (what === 'self_off' && item.isMine) toast.success(`${item.name} is out of your sessions. It stays in your library.`);
      if (what === 'self_off' && !item.isMine) toast.success(`${item.name} is out of your sessions. Nobody else is affected.`);
      if (what === 'remove') toast.success(`${item.name} is removed for everyone.`);
      await load();
    } catch (e) {
      toast.error(apiErrorMessage(e, `Could not change ${item.name}.`));
      await load();
    } finally {
      setBusy(null);
    }
  };

  if (error) return <div className="mx-auto max-w-[1000px]"><ErrorNote message={error} /></div>;
  if (!view) return <Loading label="Loading skills and plugins" />;

  // What you added is yours; everyone else's, and Agents Anywhere's, is the
  // company store. Each list is alphabetical.
  const byName = (a: LibraryItem, b: LibraryItem) => a.name.localeCompare(b.name);
  const mine = [...view.mine, ...view.company.filter((i) => i.isMine)].sort(byName);
  const store = [...view.company.filter((i) => !i.isMine), ...view.aa].sort(byName);
  let onShelf = mine;
  if (shelf === 'store') onShelf = store;
  const rows = onShelf.filter((item) => item.kind === kind);
  const shelves = [
    { id: 'mine', label: 'My own', count: mine.length },
    { id: 'store', label: 'Company store', count: store.length },
  ];
  const tabs = [
    { id: 'skill', label: `Skills (${onShelf.filter((i) => i.kind === 'skill').length})` },
    { id: 'plugin', label: `Plugins (${onShelf.filter((i) => i.kind === 'plugin').length})` },
  ];
  let noun = 'skills';
  if (kind === 'plugin') noun = 'plugins';
  let emptyTitle = `No ${noun} of your own yet`;
  let emptyReason = 'Import one from GitHub. It is on in your sessions, and everyone else at the company finds it in their Company store.';
  if (shelf === 'store') {
    emptyTitle = `No ${noun} in the company store yet`;
    emptyReason = 'What anyone else at the company imports shows here, for you to switch on.';
  }

  return (
    <div className="mx-auto max-w-[1000px]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs tabs={shelves} active={shelf} onChange={(id) => setShelf(id as Shelf)} label="My own or company store" variant="pills" className="!mb-0" />
        <button type="button" onClick={() => setImporting(true)} className="inline-flex h-10 items-center gap-1.5 rounded-act bg-ink px-4 text-[13px] font-semibold text-white hover:bg-black whitespace-nowrap">
          <Plus size={15} /> Import your own
        </button>
      </div>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <FilterChips tabs={tabs} active={kind} onChange={(id) => setKind(id as Kind)} label="Skill or plugin" />
        {shelf === 'mine' && <span className="text-[12.5px] text-ink-muted">What you add is yours here, and in everyone else&apos;s Company store.</span>}
        {shelf === 'store' && <span className="text-[12.5px] text-ink-muted">Switch on what you want in your sessions. Nothing here is on for you until you do.</span>}
      </div>
      <div className="mt-4">
        {rows.length === 0 && (
          <EmptyState title={emptyTitle} reason={emptyReason} />
        )}
        {rows.length > 0 && (
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-line">
                <th className={HEAD}>Name</th>
                <th className={`${HEAD} hidden whitespace-nowrap md:table-cell`}>Added by</th>
                <th className={`${HEAD} whitespace-nowrap`}>On for me</th>
                <th className={HEAD}><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => {
                // Whoever added it, or the Owner, may remove it.
                const canRemove = Boolean(item.canRemove);
                return (
                  <tr key={item.id} className="border-b border-line-soft">
                    <td className={CELL}>
                      <span className="flex items-center gap-3">
                        <RouteMark glyph={item.name.charAt(0).toUpperCase()} />
                        <span className="min-w-0">
                          <span className="block font-medium">{item.name}</span>
                          <span className="line-clamp-1 max-w-[44ch] text-[12.5px] text-ink-dim">{item.description}</span>
                        </span>
                      </span>
                    </td>
                    <td className={`${CELL} hidden md:table-cell`}>
                      <span className="block font-medium">{providedName(item)}</span>
                      <span className="block text-[12px] text-ink-dim">{providedLine(item)}</span>
                    </td>
                    <td className={`${CELL} w-[1%] whitespace-nowrap`}>
                      <Toggle checked={item.onForMe} disabled={busy === item.id} label={`${item.name} on for me`} onChange={(next) => change(item, selfChange(next))} />
                    </td>
                    <td className={`${CELL} w-[1%] whitespace-nowrap`}>
                      <span className="flex items-center justify-end gap-2">
                        <Action size="sm" variant="quiet" onClick={() => setOpen(item)}>Details</Action>
                        {canRemove && <Action size="sm" variant="quiet" disabled={busy === item.id} onClick={() => change(item, 'remove')}>Remove</Action>}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {importing && <ImportDialog clientId={clientId} onClose={() => setImporting(false)} onImported={() => { setImporting(false); void load(); }} />}
      {open && <CapabilityDetails clientId={clientId} item={open} canPush={canPushItem(open, view.can_manage)} onClose={() => setOpen(null)} />}
    </div>
  );
};

export default CapabilitiesPanel;
